// ─── Pillar Score Sync ────────────────────────────────────────────────────────
//
// Persists computed pillar scores to Supabase `pillar_scores` table.
// Called by the web dashboard after score computation.
// Mobile app reads from this table — never computes scores locally.
//
// This is the SINGLE SOURCE OF TRUTH for health pillar scores.
// ──────────────────────────────────────────────────────────────────────────────

import { supabase, getCurrentUser } from './supabase';
import type { PillarResult } from './pillarScoring';
import { calcCircadianMidpoint, calcBaselineMidpoint, calcCircadianZScore, interpretCircadianZ } from './circadianPhase';
import { calcSleepVariability, interpretSleepVariability } from './sleepVariability';
import type { DailyMetrics } from '../types';

interface PillarScoreRow {
  user_id: string;
  data_date: string;
  sleep: number | null;
  movement: number | null;
  nutrition: number | null;
  stress: number | null;
  social: number | null;
  substance: number | null;
  sleep_label: string | null;
  movement_label: string | null;
  nutrition_label: string | null;
  stress_label: string | null;
  social_label: string | null;
  substance_label: string | null;
  sleep_sub: string | null;
  movement_sub: string | null;
  nutrition_sub: string | null;
  sleep_inputs: number;
  movement_inputs: number;
  nutrition_inputs: number;
  stress_inputs: number;
  social_inputs: number;
  substance_inputs: number;
  // Confidence metrics
  confidence_score: number | null;
  confidence_label: string | null;
  data_points_available: number | null;
  data_points_expected: number | null;
  // Computed sleep metrics (for mobile + PDF)
  circadian_z_score: number | null;
  circadian_interpretation: string | null;
  circadian_baseline_midpoint: number | null;
  sleep_efficiency_cv: number | null;
  sleep_variability_interpretation: string | null;
}

/** Compute circadian + variability metrics from sleep history for Supabase persistence. */
function computeSleepMetrics(sleepHistory?: DailyMetrics[]): {
  circadian_z_score: number | null;
  circadian_interpretation: string | null;
  circadian_baseline_midpoint: number | null;
  sleep_efficiency_cv: number | null;
  sleep_variability_interpretation: string | null;
} {
  const defaults = {
    circadian_z_score: null, circadian_interpretation: null,
    circadian_baseline_midpoint: null, sleep_efficiency_cv: null,
    sleep_variability_interpretation: null,
  };
  if (!sleepHistory || sleepHistory.length < 5) return defaults;

  const withSleep = sleepHistory.filter(m => m.bedtime_start && m.total_sleep_mins && m.total_sleep_mins > 0);
  if (withSleep.length < 5) return defaults;

  try {
    // Circadian Z-score
    const history = withSleep.map(m => ({
      sleepOnset: new Date(m.bedtime_start!),
      wakeTime: new Date(new Date(m.bedtime_start!).getTime() + m.total_sleep_mins! * 60000),
    }));
    const baseline = calcBaselineMidpoint(history);
    if (!baseline) return defaults;

    const latest = withSleep[withSleep.length - 1];
    const latestOnset = new Date(latest.bedtime_start!);
    const latestWake = new Date(latestOnset.getTime() + latest.total_sleep_mins! * 60000);
    const todayMid = calcCircadianMidpoint(latestOnset, latestWake);
    const z = calcCircadianZScore(todayMid, baseline.median, baseline.sd);
    const interp = interpretCircadianZ(z);

    // Sleep variability
    const varData = withSleep
      .filter(m => m.sleep_efficiency != null)
      .map(m => ({
        efficiency: m.sleep_efficiency!,
        onsetTime: new Date(m.bedtime_start!),
        wakeTime: new Date(new Date(m.bedtime_start!).getTime() + m.total_sleep_mins! * 60000),
        duration: m.total_sleep_mins!,
      }));
    const variability = calcSleepVariability(varData, 14);

    return {
      circadian_z_score: Math.round(z * 100) / 100,
      circadian_interpretation: interp.label,
      circadian_baseline_midpoint: Math.round(baseline.median * 100) / 100,
      sleep_efficiency_cv: variability ? variability.efficiencyCV : null,
      sleep_variability_interpretation: variability ? interpretSleepVariability(variability.efficiencyCV).label : null,
    };
  } catch {
    return defaults;
  }
}

/**
 * Persist pillar scores to Supabase.
 * Uses upsert on (user_id, data_date) — safe to call repeatedly.
 * Runs silently in the background; never blocks the UI.
 */
export async function syncPillarScores(
  pillarResults: Record<string, PillarResult>,
  dataDate: string,
  sleepHistory?: DailyMetrics[],
): Promise<boolean> {
  try {
    const user = await getCurrentUser();
    if (!user) return false;

    const get = (key: string): PillarResult | null => pillarResults[key] ?? null;

    const row: PillarScoreRow = {
      user_id: user.id,
      data_date: dataDate,
      sleep:     get('sleep')?.score ?? null,
      movement:  get('movement')?.score ?? null,
      nutrition: get('nutrition')?.score ?? null,
      stress:    get('stress')?.score ?? null,
      social:    get('social')?.score ?? null,
      substance: get('substance')?.score ?? null,
      sleep_label:     get('sleep')?.label ?? null,
      movement_label:  get('movement')?.label ?? null,
      nutrition_label: get('nutrition')?.label ?? null,
      stress_label:    get('stress')?.label ?? null,
      social_label:    get('social')?.label ?? null,
      substance_label: get('substance')?.label ?? null,
      sleep_sub:     get('sleep')?.subMetric ?? null,
      movement_sub:  get('movement')?.subMetric ?? null,
      nutrition_sub: get('nutrition')?.subMetric ?? null,
      sleep_inputs:     get('sleep')?.inputCount ?? 0,
      movement_inputs:  get('movement')?.inputCount ?? 0,
      nutrition_inputs: get('nutrition')?.inputCount ?? 0,
      stress_inputs:    get('stress')?.inputCount ?? 0,
      social_inputs:    get('social')?.inputCount ?? 0,
      substance_inputs: get('substance')?.inputCount ?? 0,
      // Confidence (use sleep pillar as representative — most data-rich)
      confidence_score: get('sleep')?.confidence ?? null,
      confidence_label: get('sleep')?.confidenceLabel ?? null,
      data_points_available: get('sleep')?.inputCount ?? null,
      data_points_expected: 7,
      // Computed sleep metrics
      ...computeSleepMetrics(sleepHistory),
    };

    const { error } = await supabase
      .from('pillar_scores')
      .upsert(row, { onConflict: 'user_id,data_date' });

    if (error) {
      console.warn('[PillarSync] upsert error:', error.message);
      return false;
    }
    return true;
  } catch (e) {
    console.warn('[PillarSync] unexpected error:', e);
    return false;
  }
}
